import curryN from 'lodash/fp/curryN';
import range from 'lodash/range';
import get from 'lodash/get';

import * as utils from '@utils/form';
import { IMAGE_SIZE } from '@api/media/constants';
import { TEXT_COLOURS, BRANDED_COLOURS, OREGON_COLOURS, GRADIENTS, SPIKES_COLOURS } from '@constants';

export const SECTIONS_COUNT = 3;

const filterSections = (sections) =>
  sections.filter((section) => section.title || section.links?.length || section.show);

const sectionValidator = curryN(2, (validator, context) => ({
  validator: (spec) => {
    const [prefix, idx, field] = spec.field.split('.');
    const { getFieldValue } = context;
    const hasSections = filterSections(getFieldValue(prefix)).length !== 0;

    if (
      (!hasSections && idx === '0') ||
      getFieldValue([prefix, idx, 'show']) ||
      getFieldValue([prefix, idx, 'title']) ||
      getFieldValue([prefix, idx, 'links']).length !== 0
    ) {
      const message = validator(getFieldValue([prefix, idx, field]));
      if (message) {
        return Promise.reject(message);
      }
    }

    return Promise.resolve();
  },
}));

export const FORM_FIELDS = {
  TITLE: {
    name: 'title',
    label: 'Title:',
    placeholder: 'Enter a title',
    rules: [{ required: true, message: 'Title is required' }],
  },
  DESCRIPTION: {
    name: 'description',
    label: 'Description:',
    placeholder: 'Enter description',
    rules: [{ required: true, message: 'Description is required' }],
  },
  BACKGROUND_COLOR: {
    name: 'backgroundColour',
    label: 'Background colour:',
    colors: [...BRANDED_COLOURS, ...OREGON_COLOURS, ...TEXT_COLOURS, ...SPIKES_COLOURS, GRADIENTS['gradient-1'].key],
    rules: [{ required: true, message: 'Background colour is required' }],
  },
  IMAGE: {
    name: 'image',
    label: 'Featured image:',
    rules: [{ required: true, message: 'An image is required' }],
    size: IMAGE_SIZE['700x460'],
    serialize: utils.imageSerialize,
    deserialize: utils.imageDeserialize,
  },
  SECTIONS: {
    name: 'sections',
    type: 'array',
    serialize: (field, values) => ({
      ...values,
      sections: filterSections(values.sections),
    }),
    deserialize: (field, values) => {
      const sections = get(values, field.name) || [];

      if (sections.length !== SECTIONS_COUNT) {
        return {
          ...values,
          sections: [...(sections || []), ...range(SECTIONS_COUNT - sections?.length).map(() => ({ links: [] }))],
        };
      }

      return values;
    },
  },
};

export const SECTION_FIELDS = {
  SHOW: {
    name: 'show',
    label: 'Show this section',
  },
  TITLE: {
    name: 'title',
    label: 'Section title:',
    placeholder: 'Enter section title',
    rules: [sectionValidator((value) => !value && 'Section title is required')],
  },
  LINKS: {
    name: 'links',
    label: 'Current links:',
    rules: [sectionValidator((value) => value.length === 0 && 'Should have at least 1 item')],
  },
};

export const LINKS_FORM_FIELDS = {
  NAME: {
    name: 'name',
    placeholder: 'Enter the link name',
    rules: [{ required: true, message: 'Name is required' }],
  },
  PAGE: {
    entityName: 'page',
    urlName: 'url',
    placeholder: 'Enter a page name or URL',
    rules: [{ required: true, message: 'Page or URL is required' }],
  },
  FILE: {
    name: 'fileId',
    rules: [{ required: true, message: 'File is required' }],
  },
};

export const INITIAL_VALUES = {
  [FORM_FIELDS.BACKGROUND_COLOR.name]: FORM_FIELDS.BACKGROUND_COLOR.colors[0],
};
